/**
 * Shoe PDP bundle upsell (shoe + recovery slides).
 * Syncs with the main product variant picker via variant:change and adds both lines to cart.
 */
(() => {
  'use strict';

  let bundleSubmitting = false;

  function formatMoney(cents) {
    let formatted;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      formatted = window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
    } else {
      const amount = cents / 100;
      const amountStr = amount % 1 === 0 ? amount.toString() : amount.toFixed(2);
      formatted = `$${amountStr}`;
    }
    return formatted.replace(/\.00(?=\D|$)/g, '');
  }

  function getSizeOptionPosition() {
    const picker = document.querySelector(`variant-picker[data-size-option-position]`);
    return picker ? parseInt(picker.getAttribute('data-size-option-position'), 10) : 2;
  }

  function getVariantSizeLabel(variant) {
    if (!variant) return '';
    const pos = getSizeOptionPosition();
    return (variant[`option${pos}`] || variant.option2 || '').toString().trim();
  }

  class ProductShoeBundle extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('data-product-form-id');
      this.discountCents = parseInt(this.getAttribute('data-discount-cents') || '1000', 10);

      this.bundleVariantEl = this.querySelector('#bundleCurrentProductVariant');
      this.swatchRoot = this.querySelector('#pdpBundleProductSwatch');
      this.selector = this.querySelector('#pdpBundleSelector');
      this.atcButton = this.querySelector('#pdpBundleAtc');
      this.selectorError = this.querySelector('#pdpBundleSelectorError');
      this.preorderMsg = this.querySelector('.product-bundle-preorder-msg');
      this.additionalImage = this.querySelector('.pdp_bundle-additional-product .product-bundle-image');

      if (!this.swatchRoot || !this.selector || !this.atcButton) return;

      this._onVariantChange = this._onVariantChange.bind(this);
      this._onSwatchClick = this._onSwatchClick.bind(this);
      this._onSelectorChange = this._onSelectorChange.bind(this);
      this._onAtcClick = this._onAtcClick.bind(this);

      document.addEventListener('variant:change', this._onVariantChange);
      this.addEventListener('click', this._onSwatchClick);
      this.selector.addEventListener('change', this._onSelectorChange);
      this.atcButton.addEventListener('click', this._onAtcClick);

      const initialHandle = this.swatchRoot.getAttribute('data-selected-product');
      if (initialHandle) this._loadProductVariants(initialHandle);

      const picker = document.querySelector(`variant-picker[form-id="${this.formId}"]`);
      const variantScript = picker?.querySelector('script[data-variant]');
      if (variantScript?.textContent) {
        try {
          this._updateBundleVariant(JSON.parse(variantScript.textContent));
        } catch (_) {}
      }

      this._checkAtcButton();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this._onVariantChange);
    }

    _productForm() {
      if (!this.formId) return null;
      return document.getElementById(this.formId);
    }

    _onVariantChange(event) {
      const form = event.target?.closest?.('form') || event.target;
      const productForm = this._productForm();
      if (productForm && form !== productForm && !productForm.contains(form)) return;

      const variant = event.detail?.variant;
      document.querySelector('.product-info .variant-picker-size')?.classList.remove('is-bundle-size-error');
      const colorwayEl = this.querySelector('.pdp_bundle-current-product .pdp_bundle-product-variant-title');
      if (colorwayEl && variant?.option1) colorwayEl.textContent = variant.option1;
      this._updateBundleVariant(variant);

      const handle = this.swatchRoot.getAttribute('data-selected-product');
      if (handle) this._loadProductVariants(handle);
    }

    _updateBundleVariant(variant) {
      if (!this.bundleVariantEl) return;

      if (!variant || !variant.available) {
        this.bundleVariantEl.innerHTML = `
          <span class="bundleCurrentProductVariant-Select">
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="1.24041" y="0.970515" width="14.5192" height="14.5192" rx="7.25959" stroke="#FF4F43" stroke-width="1.48082"/>
              <path d="M8.80349 6.43357C8.99304 8.26979 9.08781 11.4328 9.11151 12.3806C9.12335 12.961 7.95054 12.9847 7.96238 12.3924C7.97423 11.4091 8.11639 8.26979 8.32963 6.43357C8.35332 6.22033 8.76795 6.22033 8.80349 6.43357ZM9.2892 4.79874C9.2892 5.26076 8.9575 5.52138 8.53102 5.52138C8.12824 5.52138 7.78469 5.26076 7.78469 4.79874C7.78469 4.34857 8.12824 4.09979 8.53102 4.09979C8.9575 4.09979 9.2892 4.34857 9.2892 4.79874Z" fill="#FF4F43"/>
            </svg>
            SELECT SIZE
          </span>`;
        this.bundleVariantEl.setAttribute('data-bundle-id', '0');
        this.bundleVariantEl.removeAttribute('data-current-product-price');
      } else {
        const size = getVariantSizeLabel(variant);
        this.bundleVariantEl.textContent = size ? `SIZE ${size}` : 'SIZE';
        this.bundleVariantEl.setAttribute('data-bundle-id', String(variant.id));
        this.bundleVariantEl.setAttribute('data-current-product-price', String(variant.price));
      }
      this._checkAtcButton();
    }

    _onSwatchClick(event) {
      const swatch = event.target.closest('.pdp_bundle-product-swatch');
      if (!swatch || !this.contains(swatch)) return;

      const productHandle = swatch.getAttribute('data-product-handle');
      const productImage = swatch.getAttribute('data-product-image');
      const productPrice = swatch.getAttribute('data-product-price');

      this.swatchRoot.setAttribute('data-selected-product', productHandle);
      if (productPrice) this.swatchRoot.setAttribute('data-selected-product-price', productPrice);

      this.querySelectorAll('.pdp_bundle-product-swatch-border').forEach((el) => el.classList.remove('active'));
      swatch.closest('.pdp_bundle-product-swatch-border')?.classList.add('active');

      if (productImage && this.additionalImage) {
        this.additionalImage.style.backgroundImage = `url('${productImage}')`;
      }
      if (this.preorderMsg) this.preorderMsg.textContent = '';

      this._loadProductVariants(productHandle);
      setTimeout(() => this._checkAtcButton(), 500);
    }

    _onSelectorChange() {
      this._checkAtcButton();
      if (!this.selector.value) {
        this.selector.style.backgroundColor = '#A1A1A1';
      } else {
        this.selector.style.backgroundColor = '#1f4477';
      }

      const selectedOption = this.selector.options[this.selector.selectedIndex];
      const slideSize = selectedOption?.textContent?.replace(/^Size\s+/i, '').trim();
      if (slideSize) sessionStorage.setItem('slidesize', slideSize);

      if (this.preorderMsg) {
        this.preorderMsg.textContent = selectedOption?.hasAttribute('data-preorder-message') ? 'Pre order' : '';
      }

      this.selector.classList.remove('error');
      if (this.selectorError) this.selectorError.style.display = 'none';
    }

    _onAtcClick() {
      this._checkAtcButton();
      if (!this.atcButton.disabled) {
        this._addToCart();
        return;
      }

      const shoeVariantId = this.bundleVariantEl?.getAttribute('data-bundle-id');
      if (shoeVariantId && shoeVariantId !== '0') {
        this.selector.classList.add('error');
        if (this.selectorError) this.selectorError.style.display = 'block';
      } else {
        const sizeFieldset = document.querySelector('.product-info .variant-picker-size');
        if (sizeFieldset) {
          sizeFieldset.classList.add('is-bundle-size-error');
          sizeFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }

    async _loadProductVariants(productHandle) {
      if (!productHandle || !this.selector) return;

      try {
        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) throw new Error('Product fetch failed');
        const productData = await response.json();

        this.selector.innerHTML = '<option value="">SELECT SIZE</option>';
        productData.variants.forEach((variant) => {
          if (!variant.available) return;
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = `Size ${variant.option2 || variant.title}`;
          this.selector.appendChild(option);
        });
        this.selector.style.backgroundColor = '#A1A1A1';

        if (productData.featured_image && this.additionalImage) {
          this.additionalImage.style.backgroundImage = `url('${productData.featured_image}')`;
        }

        await this._attachPreorderDates(productHandle);
        this._autoSelectSlideSize();
      } catch (error) {
        console.error('[product-shoe-bundle] load variants failed:', error);
      }
    }

    async _attachPreorderDates(productHandle) {
      try {
        const response = await fetch(`/products/${productHandle}`);
        if (!response.ok) return;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const productJsonElement = doc.querySelector('[data-product-json]');
        if (!productJsonElement) return;

        const data = JSON.parse(productJsonElement.textContent);
        const inventories = data.inventories || {};
        this.selector.querySelectorAll('option').forEach((option) => {
          const inventory = inventories[option.value];
          if (
            inventory &&
            inventory.inventory_management != null &&
            inventory.inventory_quantity - (inventory['preorder-limit'] || 0) <= 0 &&
            inventory['preorder-date'] != null
          ) {
            option.setAttribute('data-preorder-message', inventory['preorder-date']);
          }
        });
      } catch (_) {
        /* preorder metadata optional on new theme */
      }
    }

    _autoSelectSlideSize() {
      setTimeout(() => {
        let targetSize = sessionStorage.getItem('slidesize') || sessionStorage.getItem('size');
        if (!targetSize) return;
        targetSize = targetSize.replace('.5', '');

        let matched = false;
        this.selector.querySelectorAll('option').forEach((option) => {
          const size = option.textContent.replace(/^Size\s+/i, '').trim();
          if (size === targetSize) {
            this.selector.value = option.value;
            this.selector.style.backgroundColor = '#1f4477';
            this.selector.dispatchEvent(new Event('change', { bubbles: true }));
            matched = true;
          }
        });

        if (!matched) {
          this.atcButton.disabled = true;
        }
      }, 250);
    }

    _checkAtcButton() {
      const bundleVariantId = this.bundleVariantEl?.getAttribute('data-bundle-id');
      const selectedVariantId = this.selector?.value;
      const currentProductPrice = parseFloat(this.bundleVariantEl?.getAttribute('data-current-product-price') || '', 10);
      const selectedProductPrice = parseFloat(this.swatchRoot?.getAttribute('data-selected-product-price') || '', 10);

      if (bundleVariantId && bundleVariantId !== '0' && selectedVariantId) {
        if (!Number.isNaN(currentProductPrice) && !Number.isNaN(selectedProductPrice)) {
          const compareCents = currentProductPrice + selectedProductPrice;
          const saleCents = compareCents - this.discountCents;
          this.atcButton.innerHTML = `ADD BUNDLE TO BAG - ${formatMoney(saleCents)} <s><em>${formatMoney(compareCents)}</em></s>`;
        } else {
          this.atcButton.textContent = 'ADD BUNDLE TO BAG';
        }
        this.atcButton.disabled = false;
      } else {
        this.atcButton.disabled = true;
      }
    }

    async _addToCart() {
      if (bundleSubmitting) return;

      const bundleVariantId = this.bundleVariantEl?.getAttribute('data-bundle-id');
      const selectedVariantId = this.selector?.value;
      if (!bundleVariantId || bundleVariantId === '0' || !selectedVariantId) return;

      const selectedOption = this.selector.options[this.selector.selectedIndex];
      const items = [{ id: parseInt(bundleVariantId, 10), quantity: 1 }];
      const slideItem = { id: parseInt(selectedVariantId, 10), quantity: 1 };

      if (selectedOption?.hasAttribute('data-preorder-message')) {
        slideItem.properties = { 'Estimated Shipping Date': selectedOption.getAttribute('data-preorder-message') };
      }
      items.push(slideItem);

      bundleSubmitting = true;
      this.atcButton.disabled = true;
      const previousHtml = this.atcButton.innerHTML;
      this.atcButton.textContent = 'Adding…';

      try {
        const resp = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.description || `HTTP ${resp.status}`);
        }

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          if (typeof drawer.show === 'function') drawer.show();
          else if (typeof drawer.open === 'function') drawer.open();
          this.atcButton.innerHTML = previousHtml;
          this.atcButton.disabled = false;
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (err) {
        console.error('[product-shoe-bundle] add failed:', err);
        this.atcButton.innerHTML = previousHtml;
        this.atcButton.disabled = false;
        alert(`Couldn't add this bundle to your cart: ${err.message || 'please try again'}`);
      } finally {
        bundleSubmitting = false;
      }
    }
  }

  if (!window.customElements.get('product-shoe-bundle')) {
    window.customElements.define('product-shoe-bundle', ProductShoeBundle);
  }
})();
